<template>
  <b-modal
    id="modal-bulk-upload-tna"
    ref="modalBulkUpload"
    title="Bulk TNA - Upload Excel"
    size="lg"
    @hidden="resetModal"
    @ok="handleUpload">
    <div>
      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <i class="fe fe-info me-2"></i>
        <strong>Petunjuk:</strong>
        <ul class="mb-0 mt-2">
          <li>Upload file Excel yang sudah diisi sesuai template (.xlsx)</li>
          <li>Ukuran file maksimal 5MB</li>
        </ul>
      </div>

      <!-- File Upload -->
      <div class="form-group">
        <label class="form-label">File Excel <span class="text-danger">*</span></label>
        <div class="custom-file">
          <input 
            type="file" 
            class="custom-file-input" 
            id="excelFileTna"
            accept=".xlsx"
            @change="handleFileChange"
            ref="fileInput">
          <label class="custom-file-label" for="excelFileTna">
            {{ selectedFileName || 'Pilih file Excel...' }}
          </label>
        </div>
        <small class="form-text text-muted">
          Format yang diterima: .xlsx saja (maksimal 5MB)
        </small>
      </div>

      <!-- Selected File Info -->
      <div v-if="selectedFile" class="alert alert-success mt-3">
        <i class="fe fe-file-text me-2"></i>
        <strong>File dipilih:</strong> {{ selectedFile.name }}
        <br>
        <small>Ukuran: {{ formatFileSize(selectedFile.size) }}</small>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <button 
        type="button" 
        class="btn btn-secondary" 
        @click="cancel()">
        Batal
      </button>
      <button 
        type="button" 
        class="btn btn-primary"
        :disabled="!selectedFile || isUploading"
        @click="ok()">
        <span v-if="isUploading">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Mengupload...
        </span>
        <span v-else>
          <i class="fe fe-upload me-2"></i>
          Upload
        </span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { buildScopedCompanyMessage, normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalBulkUploadTna',
  props: {
    selectedCompany: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      isUploading: false
    };
  },
  methods: {
    ...mapActions('tnaMitra', ['bulkUploadTna']),
    
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.name.endsWith('.xlsx')) {
          this.$swal({
            title: 'Tipe File Tidak Valid',
            text: 'Pilih file Excel dengan format .xlsx',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal({
            title: 'File Terlalu Besar',
            text: 'Ukuran file tidak boleh lebih dari 5MB.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },
    
    resetFile() {
      this.selectedFile = null;
      this.selectedFileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    resetModal() {
      this.resetFile();
      this.isUploading = false;
    },
    
    async handleUpload(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault();
      
      if (!this.selectedFile) {
        this.$swal({
          title: 'File Belum Dipilih',
          text: 'Pilih file Excel terlebih dahulu',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isUploading = true;

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        // Call API
        const result = await this.bulkUploadTna({
          formData,
          params: this.selectedCompany ? { company: this.selectedCompany } : {}
        });

        // Close upload modal
        this.$refs.modalBulkUpload.hide();

        // Build HTML content for swal
        const htmlContent = this.buildResultHtml(result);
        const errors = result?.data?.errors || [];

        // Show success message with data mapping
        this.$swal({
          title: errors.length ? 'Upload Selesai dengan Error' : 'Upload Berhasil',
          html: htmlContent,
          icon: errors.length ? 'warning' : 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: errors.length ? '#f6c343' : '#00c9a7',
          width: errors.length ? '1100px' : '720px'
        });

        // Emit success to refresh data
        this.$emit('upload-success', result);
      } catch (error) {
        const errorMessage = normalizeErrorMessage(error, 'File belum berhasil diupload. Periksa format file lalu coba lagi.');
        
        this.$swal({
          title: 'Upload Gagal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isUploading = false;
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    buildResultHtml(result) {
      const message = normalizeErrorMessage(result.message || 'Upload TNA selesai diproses.', 'Upload TNA selesai diproses.');
      const data = result.data || {};
      
      let html = `<p style="margin-bottom: 16px;">${message}</p>`;
      
      html += `<div style="text-align: left; background: #f8f9fa; padding: 16px; border-radius: 8px; max-height: 460px; overflow: auto;">`;

      // Handle submitted data
      if (data.submitted && data.submitted.length > 0) {
        const totalSubmitted = data.submitted.reduce((sum, item) => sum + (item.data_count || 0), 0);
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #00c9a7;"><strong><i class="fe fe-check-circle"></i> Berhasil diajukan (${totalSubmitted} data)</strong></p>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #e8f5e9;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Training ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Jumlah Data</th>
                </tr>
              </thead>
              <tbody>
        `;
        data.submitted.forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.training_id}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.data_count}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }

      // Handle cancelled data
      if (data.cancelled && data.cancelled.length > 0) {
        const totalCancelled = data.cancelled.reduce((sum, item) => sum + (item.data_count || 0), 0);
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #f6c343;"><strong><i class="fe fe-alert-circle"></i> Dibatalkan (${totalCancelled} data)</strong></p>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #fff8e1;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Training ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Jumlah Data</th>
                </tr>
              </thead>
              <tbody>
        `;
        data.cancelled.forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.training_id}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.data_count}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }

      // Handle errors
      if (data.errors && data.errors.length > 0) {
        html += `
          <div style="margin-bottom: 16px;">
            <p style="margin-bottom: 8px; color: #e63757;"><strong><i class="fe fe-x-circle"></i> Detail error (${data.errors.length} baris)</strong></p>
            <table style="width: 100%; min-width: 900px; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #ffebee;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Row</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Sheet</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">NIK</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Training ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pesan Error</th>
                </tr>
              </thead>
              <tbody>
        `;
        data.errors.slice(0, 50).forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.row || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.sheet || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.nik || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.training_id || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd; color: #e63757;">${this.escapeHtml(buildScopedCompanyMessage(item))}</td>
            </tr>
          `;
        });
        html += `</tbody></table>`;
        if (data.errors.length > 50) {
          html += `<p class="mb-0 mt-2 text-muted">Masih ada ${data.errors.length - 50} error lain. Perbaiki error dari atas dulu lalu upload ulang.</p>`;
        }
        html += `</div>`;
      }

      // If no data at all
      if ((!data.submitted || data.submitted.length === 0) && 
          (!data.cancelled || data.cancelled.length === 0) && 
          (!data.errors || data.errors.length === 0)) {
        html += `<p style="text-align: center; color: #6c757d;">Tidak ada data yang diproses.</p>`;
      }

      html += `</div>`;

      return html;
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.custom-file-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
